import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, BookOpenIcon, ClockIcon, HeartIcon } from 'lucide-react';
import ArticleCard from '../../components/Articles/ArticleCard';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
import { SearchWithAutocomplete } from '../../components/UI/SearchWithAutocomplete';
import { useDarkMode } from '../../context/DarkModeContext';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { useDarkClasses } from '../../hooks/useDarkClasses';
interface Article {
  id: number;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  readTime: string;
  image?: string;
  featured?: boolean;
}
const romanceArticles: Article[] = [{
  id: 1,
  title: 'The Evolution of Romance Tropes in 2023',
  excerpt: 'How classic romance tropes are being reimagined for modern readers — from enemies-to-lovers to found family dynamics that feel fresh and earned.',
  author: 'Emma Clarke',
  date: 'October 9, 2023',
  category: 'Trends',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1474552226712-ac0f0961a954?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  featured: true
}, {
  id: 2,
  title: 'Diversity in Romance: Beyond the Mainstream',
  excerpt: 'Exploring the growing diversity in romance fiction and its impact on readers who finally see themselves in the stories they love.',
  author: 'Priya Sharma',
  date: 'October 6, 2023',
  category: 'Representation',
  readTime: '9 min read',
  image: 'https://images.unsplash.com/photo-1516575334481-f85287c2c82d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 3,
  title: 'Cover Design Trends Dominating Indie Romance',
  excerpt: "Illustrated covers, bold typography, and character art are reshaping romance shelves. A visual breakdown of what's working in 2023.",
  author: 'Luna Design Co.',
  date: 'September 22, 2023',
  category: 'Industry',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 4,
  title: 'Writing Steam: Balancing Emotion and Heat',
  excerpt: 'A guide to crafting intimate scenes that drive character development and plot forward, rather than just filling pages.',
  author: 'Sarah J. Miller',
  date: 'September 15, 2023',
  category: 'Craft',
  readTime: '11 min read',
  image: 'https://images.unsplash.com/photo-1518199266791-5375a83190b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 5,
  title: 'The Rise of Monster Romance',
  excerpt: 'Why readers are falling for creatures from the deep, aliens, and mythical beasts. An analysis of the subgenre explosion.',
  author: 'Becca Thorne',
  date: 'September 10, 2023',
  category: 'Trends',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1608889825205-eebdb9fc5806?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}, {
  id: 6,
  title: 'Marketing Romance on TikTok: A Case Study',
  excerpt: 'How one author went from zero to bestseller using BookTok trends and authentic engagement.',
  author: 'Marketing Maven',
  date: 'September 5, 2023',
  category: 'Marketing',
  readTime: '10 min read',
  image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
}];
const CATEGORIES = ['All', 'Trends', 'Representation', 'Industry', 'Craft', 'Marketing'];
const READ_TIMES = ['All', 'Under 8 min', '8–12 min', '12+ min'];
const parseMinutes = (readTime: string): number => {
  const match = readTime.match(/(\d+)/);
  return match ? parseInt(match[1]) : 0;
};
const RomanceArticles = () => {
  const {
    darkMode
  } = useDarkMode();
  const rt = useRoleTheme();
  const dk = useDarkClasses();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeReadTime, setActiveReadTime] = useState('All');
  const featured = romanceArticles.find(a => a.featured)!;
  const rest = romanceArticles.filter(a => !a.featured);
  const filtered = useMemo(() => {
    return rest.filter(article => {
      const matchesSearch = !searchQuery || article.title.toLowerCase().includes(searchQuery.toLowerCase()) || article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) || article.author.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = activeCategory === 'All' || article.category === activeCategory;
      const mins = parseMinutes(article.readTime);
      const matchesReadTime = activeReadTime === 'All' || activeReadTime === 'Under 8 min' && mins < 8 || activeReadTime === '8–12 min' && mins >= 8 && mins <= 12 || activeReadTime === '12+ min' && mins > 12;
      return matchesSearch && matchesCategory && matchesReadTime;
    });
  }, [searchQuery, activeCategory, activeReadTime, rest]);
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    value: activeCategory,
    onChange: val => setActiveCategory(val),
    options: CATEGORIES.map(c => ({
      value: c,
      label: c
    }))
  }, {
    id: 'readtime',
    title: 'Read Time',
    type: 'radio',
    value: activeReadTime,
    onChange: val => setActiveReadTime(val),
    options: READ_TIMES.map(r => ({
      value: r,
      label: r
    }))
  }];
  const handleReset = () => {
    setActiveCategory('All');
    setActiveReadTime('All');
    setSearchQuery('');
  };
  return <TroveLayout filterSections={filterSections} onResetFilters={handleReset} data-id="element-17248" data-mp="1aflgj7ydygyj">
      {/* Header */}
      <div className="mb-8" data-id="element-17249" data-mp="33xiyrfp510q4">
        <div className="flex items-center gap-2 mb-2" data-id="element-17250" data-mp="33x96tmkj8jhs">
          <BookOpenIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-17251" data-mp="38orbolpof481" />
          <Link to="/trove" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-17252" data-mp="38q2dwh3h43x1">
            The Trove
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-17253" data-mp="2uvjhqne4hwn6" />
          <Link to="/trove/romance" className={`text-sm font-medium ${rt.accentLink}`} data-id="element-17254" data-mp="2yzhewsjw0xdl">
            Romance
          </Link>
          <ChevronRightIcon className={`h-4 w-4 ${dk.textMuted}`} data-id="element-17255" data-mp="3ro5592jhucy0" />
          <span className={`text-sm ${dk.textMuted}`} data-id="element-17256" data-mp="3rl97be7nbo9k">Articles</span>
        </div>
        <div className="flex items-center gap-3 mb-3" data-id="element-17257" data-mp="3dnkpkf8vkx8q">
          <HeartIcon className="h-8 w-8 text-pink-500" data-id="element-17258" data-mp="3im5ng80nn4u0" />
          <h1 className={`text-3xl sm:text-4xl font-bold ${dk.textPrimary}`} data-id="element-17259" data-mp="3iorv07njpkc8">
            Romance Articles
          </h1>
        </div>
        <p className={`text-base max-w-2xl mb-6 ${dk.textMuted}`} data-id="element-17260" data-mp="278besc462zob">
          Deep dives into the romance genre, from craft tips and market trends
          to representation and reader insights.
        </p>
        <div className="max-w-md" data-id="element-17261" data-mp="1upxt3jdfwn4p">
          <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} placeholder="Search romance articles..." variant="pill" suggestions={[]} data-id="element-17262" data-mp="1uowifg0c62ni" />
        </div>
      </div>

      <div className="space-y-10" data-id="element-17263" data-mp="1fzqmq20piukd">
        {/* Featured Article */}
        {!searchQuery && activeCategory === 'All' && activeReadTime === 'All' && <section data-id="element-17264" data-mp="1pq32lgxktzk6">
              <h2 className={`text-lg font-semibold mb-4 ${dk.textPrimary}`} data-id="element-17265" data-mp="1pql9d7hv7w6c">
                Featured
              </h2>
              <article className={`${dk.cardSubtle} rounded-xl shadow-sm border overflow-hidden`} data-id="element-17266" data-mp="1kybjfrgr2w42">
                <div className="md:flex" data-id="element-17267" data-mp="x7pe47y4rpxr">
                  <div className="md:w-2/5" data-id="element-17268" data-mp="sb7efmhnqhsa">
                    <img src={featured.image} alt={featured.title} className="w-full h-52 md:h-full object-cover" data-id="element-17269" data-mp="saffq2srk3xs" />
                  </div>
                  <div className="md:w-3/5 p-6 flex flex-col justify-between" data-id="element-17270" data-mp="bsabrnbdc27v">
                    <div data-id="element-17271" data-mp="go8yz7kyne6p">
                      <div className="flex items-center gap-2 mb-3" data-id="element-17272" data-mp="lon6moingm47">
                        <span className={`px-2.5 py-0.5 text-white text-xs font-medium rounded-full ${rt.badgeBg}`} data-id="element-17273" data-mp="lpyaeoc3htvb">
                          {featured.category}
                        </span>
                        <span className={`text-xs ${dk.textMuted}`} data-id="element-17274" data-mp="zfi7bdizvwwp">
                          {featured.date}
                        </span>
                      </div>
                      <h3 className={`text-xl font-bold mb-2 leading-snug ${dk.textPrimary}`} data-id="element-17275" data-mp="14gz715pqtdbp">
                        {featured.title}
                      </h3>
                      <p className={`text-sm mb-4 line-clamp-3 ${dk.textSecondary}`} data-id="element-17276" data-mp="2xxvgxgqv5op9">
                        {featured.excerpt}
                      </p>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-17277" data-mp="2owdqp5jqs6mu">
                      <div className="text-xs" data-id="element-17278" data-mp="2jy2iez93i46q">
                        <p className={`font-semibold ${dk.textHeading}`} data-id="element-17279" data-mp="2jzdtlk4vee7v">
                          {featured.author}
                        </p>
                        <p className={`flex items-center gap-1 mt-0.5 ${dk.textMuted}`} data-id="element-17280" data-mp="3cmrkb0hz6djj">
                          <ClockIcon className="h-3 w-3" data-id="element-17281" data-mp="37uzsynqzw3sc" />
                          {featured.readTime}
                        </p>
                      </div>
                      <button className={`text-sm font-medium ${rt.accentLink}`} data-id="element-17282" data-mp="46y4yozdr842">
                        Read full article →
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </section>}

        {/* Category quick-filters (inline chips) */}
        <div className="flex flex-wrap gap-2" data-id="element-17283" data-mp="1m7gu4styliot">
          {CATEGORIES.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${activeCategory === cat ? 'bg-pink-100 text-pink-700 ring-1 ring-pink-200 dark:bg-pink-900/40 dark:text-pink-300 dark:ring-pink-700' : darkMode ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 border border-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-17284" data-mp="213y3xfmi2p99">
              {cat}
            </button>)}
        </div>

        {/* Results count */}
        <div className="flex items-center justify-between" data-id="element-17285" data-mp="2sum2cjwshgj8">
          <p className={`text-sm ${dk.textMuted}`} data-id="element-17286" data-mp="2osh75gqp0buh">
            {filtered.length} article{filtered.length !== 1 ? 's' : ''}
            {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
            {searchQuery ? ` matching "${searchQuery}"` : ''}
          </p>
          {(activeCategory !== 'All' || activeReadTime !== 'All' || searchQuery) && <button onClick={handleReset} className={`text-xs font-medium ${rt.accentLink}`} data-id="element-17287" data-mp="33ewfvss2sdu4">
              Clear filters
            </button>}
        </div>

        {/* Articles Grid */}
        {filtered.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-17288" data-mp="2ti1jpgj9ypj0">
            {filtered.map(article => <ArticleCard key={article.id} article={article} data-id="element-17289" data-mp="2jqx54hxih6op" />)}
          </div> : <div className={`text-center py-16 rounded-xl border border-dashed ${darkMode ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`} data-id="element-17290" data-mp="25rfmyleq0rob">
            <BookOpenIcon className={`h-8 w-8 mx-auto mb-3 opacity-30 ${dk.textMuted}`} data-id="element-17291" data-mp="2as3d74v4l4hh" />
            <p className={`text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-17292" data-mp="bjk71p8ro1w7">
              No articles found
            </p>
            <p className={`text-xs mb-4 ${dk.textMuted}`} data-id="element-17293" data-mp="7gxbbd8z5wtq">
              Try adjusting your filters or search term
            </p>
            <button onClick={handleReset} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-17294" data-mp="2k6x6vgqdbay">
              Clear all filters
            </button>
          </div>}
      </div>
    </TroveLayout>;
};
export default RomanceArticles;